import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Snackbar,
    TextField,
    Typography
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';

const METRIC_TO_TRACKING_TYPE = {
    'peso': 'weight',
    'peso corporal': 'weight',
    'weight': 'weight',
    'imc': 'bmi',
    'índice de masa corporal': 'bmi',
    'bmi': 'bmi',
    'grasa corporal': 'bodyfat',
    'porcentaje de grasa corporal': 'bodyfat',
    'bodyfat': 'bodyfat',
    '% grasa': 'bodyfat',
};

const getTrackingType = (metricName) => {
    const normalizedName = metricName?.toLowerCase().trim() || '';
    return METRIC_TO_TRACKING_TYPE[normalizedName] || null;
};

const buildMetricOptions = (userMetrics = []) => {
    const addedTrackingTypes = new Set();

    return userMetrics
        .map(userMetric => {
            const metricType = userMetric.metricType;
            if (!metricType) return null;

            const normalizedName = metricType.name?.toLowerCase() || '';
            const trackingType = getTrackingType(normalizedName);
            const label = normalizedName === 'peso corporal' ? 'Peso' : metricType.name;

            if (trackingType) {
                if (addedTrackingTypes.has(trackingType)) return null;
                addedTrackingTypes.add(trackingType);
            }

            return {
                id: metricType._id,
                label,
                unit: metricType.unit,
                trackingType: trackingType || 'custom'
            };
        })
        .filter(Boolean);
};

const QuickMeasurementDialog = ({ open, onClose, userMetrics = [], onSaved }) => {
    const { authToken } = useAuth();
    const [valuesByMetric, setValuesByMetric] = useState({});
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const metricOptions = useMemo(() => buildMetricOptions(userMetrics), [userMetrics]);
    const filledMetrics = metricOptions.filter(metric => valuesByMetric[metric.id]?.trim());

    useEffect(() => {
        if (!open) {
            setValuesByMetric({});
            setDate(new Date().toISOString().split('T')[0]);
        }
    }, [open]);

    const handleMetricValueChange = (metricId, nextValue) => {
        setValuesByMetric(prev => ({
            ...prev,
            [metricId]: nextValue
        }));
    };

    const saveMetric = async (metric, numericValue) => {
        if (metric.trackingType !== 'custom') {
            const body = { date };
            if (metric.trackingType === 'weight') body.weight = numericValue;
            if (metric.trackingType === 'bmi') body.bmi = numericValue;
            if (metric.trackingType === 'bodyfat') body.bodyFatPercentage = numericValue;

            const response = await fetch(`${import.meta.env.VITE_API_URL}/tracking/${metric.trackingType}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify(body),
            });

            if (!response.ok) throw new Error(`Error al guardar ${metric.label}`);
            return;
        }

        const response = await fetch(`${import.meta.env.VITE_API_URL}/metrics/metric-entries`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({
                metricTypeId: metric.id,
                value: numericValue,
                date,
            }),
        });

        if (!response.ok) throw new Error(`Error al guardar ${metric.label}`);
    };

    const handleSave = async () => {
        if (filledMetrics.length === 0) return;

        setSaving(true);
        try {
            const payload = filledMetrics.map(metric => {
                const numericValue = parseFloat(valuesByMetric[metric.id]);
                if (!Number.isFinite(numericValue)) throw new Error(`Valor inválido en ${metric.label}`);
                return { metric, numericValue };
            });

            await Promise.all(payload.map(({ metric, numericValue }) => saveMetric(metric, numericValue)));

            setSnackbar({
                open: true,
                message: payload.length === 1 ? 'Medición guardada correctamente' : `${payload.length} mediciones guardadas correctamente`,
                severity: 'success'
            });
            setValuesByMetric({});
            onSaved?.();
            onClose?.();
        } catch (error) {
            console.error('Error saving measurement:', error);
            setSnackbar({ open: true, message: 'Error al guardar la medición', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 800, color: 'var(--text-primary)' }}>
                    Registrar medición
                </DialogTitle>
                <DialogContent>
                    {metricOptions.length === 0 ? (
                        <Box py={2}>
                            <Typography variant="body2" color="var(--text-secondary)">
                                No tienes métricas activas para registrar. Activa tus métricas en tu perfil para poder añadir registros rápidos.
                            </Typography>
                        </Box>
                    ) : (
                        <Box display="flex" flexDirection="column" gap={2.5} pt={1}>
                            <TextField
                                label="Fecha"
                                type="date"
                                value={date}
                                onChange={(event) => setDate(event.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />

                            <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
                                {metricOptions.map((metric, index) => (
                                    <TextField
                                        key={metric.id}
                                        label={`${metric.label}${metric.unit ? ` (${metric.unit})` : ''}`}
                                        type="number"
                                        value={valuesByMetric[metric.id] || ''}
                                        onChange={(event) => handleMetricValueChange(metric.id, event.target.value)}
                                        placeholder="Dejar en blanco para omitir"
                                        fullWidth
                                        autoFocus={index === 0}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={onClose} sx={{ color: 'var(--text-secondary)' }}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={filledMetrics.length === 0 || saving}
                        sx={{
                            background: 'var(--gradient-primary)',
                            borderRadius: 'var(--border-radius-full)',
                            fontWeight: 800,
                            px: 3
                        }}
                    >
                        {saving ? 'Guardando...' : filledMetrics.length > 1 ? `Guardar ${filledMetrics.length} mediciones` : 'Guardar medición'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default QuickMeasurementDialog;
